/* eslint-disable no-unused-vars */
import ImageIcon from '@mui/icons-material/Image';
import InfoIcon from '@mui/icons-material/Info';
import {
  Box,
  CircularProgress,
  Paper,
  Tab,
  Tabs,
  Typography
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import PackageDetailsBasicInformationTab from '../components/package_details/PackageDetailBasicInformationTab';
import PackageDetailsImageDialog from '../components/package_details/PackageDetailsImageDialog';
import PackageDetailsMediaTab from '../components/package_details/PackageDetailsMediaTab';
import fileManagerService from '../services/file-manager-service';
import packageManagerService from '../services/package-manager-service';

export default function PackageDetailsPage() {
  const [packageDetails, setPackageDetails] = useState({});
  const [loading, setLoading] = useState(true);
  const { id } = useParams();
  const [activeTab, setActiveTab] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxImage, setLightboxImage] = useState('');
  const [zoomLevel, setZoomLevel] = useState(600);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const openLightbox = (imgUrl) => {
    setLightboxImage(imgUrl);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setLightboxImage('');
    setZoomLevel(600);
  };

  useEffect(() => {
    const fetchPackageDetails = async () => {
      try {
        const response = await packageManagerService.getPackageDetails(id);
        if (response.success) {
          setPackageDetails(response.result.package);
        } else {
          console.error('Error:', response.message);
        }
      } catch (error) {
        console.error('Fetch error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchPackageDetails();
  }, [id]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="60vh"
      >
        <CircularProgress size={50} />
      </Box>
    );
  }

  const handleDownload = async (imageUrl) => {
    try {
      await fileManagerService.downloadAndSaveFile(imageUrl);
    } catch (error) {
      console.log('Download error:', error);
      alert('File download failed.');
    }
  };

  const overviewItems = [
    { title: 'ID', value: id },
    { title: 'Company Name', value: packageDetails.company_name },
    { title: 'Direction Type', value: packageDetails.direction_type },
    { title: 'Tracking Number', value: packageDetails.tracking_number },
    {
      title: 'Order Number',
      value: packageDetails.order_number ?? 'N/A'
    },
    {
      title: 'Recipient Name',
      value: packageDetails.recipient_name || 'N/A'
    },
    {
      title: 'Uploaded By',
      value: packageDetails.user?.username || 'N/A'
    },
    {
      title: 'Remarks',
      value: packageDetails.remarks || 'N/A'
    }
  ];

  const collapsibleItems = [
    {
      label: 'Scanned At Coordinates',
      items: [
        packageDetails.scanned_at_latitude,
        packageDetails.scanned_at_longitude
      ]
    },
    {
      label: 'Addresses',
      items: packageDetails.address_list
    },
    {
      label: 'Other Details',
      items: packageDetails.other_details
    },
    {
      label: 'Barcodes',
      items: packageDetails.barcode_list
    }
  ];

  return (
    <div>
      <Box
        sx={{
          p: { xs: 2, md: 4 },
          backgroundColor: '#f8f9fa',
          minHeight: '100vh'
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ fontWeight: 600 }}>
          Package Details
        </Typography>

        <Paper elevation={4} sx={{ borderRadius: 3, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            textColor="primary"
            indicatorColor="primary"
            variant="fullWidth"
            sx={{ backgroundColor: 'white' }}
          >
            <Tab label="Overview" icon={<InfoIcon />} iconPosition="start" />
            <Tab label="Media" icon={<ImageIcon />} iconPosition="start" />
          </Tabs>

          <Box sx={{ p: 3, backgroundColor: '#ffffff' }}>
            {activeTab === 0 && (
              <PackageDetailsBasicInformationTab
                id={id}
                overviewItems={overviewItems}
                collapsibleItems={collapsibleItems}
              />
            )}

            {activeTab === 1 && (
              <PackageDetailsMediaTab
                imageList={packageDetails.image_path_list || []}
                videoList={packageDetails.video_path_list || []}
                onImageClick={openLightbox}
              />
            )}
          </Box>
        </Paper>

        {/* Lightbox Dialog */}
        <PackageDetailsImageDialog
          open={lightboxOpen}
          onClose={closeLightbox}
          imageUrl={lightboxImage}
          zoomLevel={zoomLevel}
          onZoomIn={() => setZoomLevel((prev) => prev + 50)}
          onZoomOut={() => setZoomLevel((prev) => Math.max(100, prev - 50))}
          onDownload={() => handleDownload(lightboxImage)}
        />
      </Box>
    </div>
  );
}
